/**
 * @title Comments form
 * @description The form where the consumer can write comments about the items
 */
import {Box} from "@mui/material";
import {Controller, FormProvider, useFieldArray, useForm, useWatch} from "react-hook-form";
import {StyledTypography} from "../../../providers/layout/LayoutProvider";
import * as React from "react";
import {useEffect, useMemo} from "react";
import {PubSub} from "../../../utility/PubSub";
import {FormInputCheckbox} from "../../../components/FormInputCheckbox";
import {useRecoilValue} from "recoil";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";

type CommentsFormProps = {
   data: CommentData;
   onSave: (data: CommentData) => Promise<void>;
};

export type CommentItemData = {
   potentialId: string
   comment: string
   image: string
   name: string
   color: string
   size: string
}

export type CommentData = {
   commentsAccept: boolean
   items: CommentItemData[]
}

export const CommentsForm = ({data, onSave}: CommentsFormProps) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [animation, setAnimation] = React.useState<boolean>(false);

   const form = useForm<CommentData>({
      defaultValues: data,
   });
   const {control, handleSubmit, trigger, getValues, formState: {isValid}} = form;

   const {fields} = useFieldArray({
      control,
      name: "items",
   });

   // Watch the 'items' array to monitor changes to the comments
   const watchedItems = useWatch({
      control,
      name: "items",
   });

   // Check if at least one comment has been written
   const hasComments = useMemo(() => {
      return watchedItems.some((item: CommentItemData) => item.comment && item.comment.trim().length > 0);
   }, [watchedItems]);

   const onSubmit = async (data: CommentData) => {
      await onSave(data);
   }

   useEffect(() => {
      return PubSub.on('commentspage-valid', async () => {
         const formValid = await trigger();
         const accept = localizations.commentsPageAccept ? getValues('commentsAccept') : true;

         if (!hasComments || (hasComments && formValid && accept)) {
            await handleSubmit(onSubmit)();
            return true;
         }
         return false;
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [hasComments, isValid]);

   useEffect(() => {
      return PubSub.on('navigation-blocked', () => {
         setAnimation(true);

         setTimeout(() => {
            setAnimation(false);
         }, 1500)
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   return (
      <Box sx={{
         width: '80%',
         margin: 'auto',
      }}>
         <FormProvider {...form}>
            {fields.map((option, index) => (
               <Box
                  key={option.id}
                  sx={{
                     display: "flex",
                     flexDirection: "row",
                     width: "100%", // Box takes 100% of the page width
                     gap: 2,
                     alignItems: "stretch", // Stretch items to match height
                     mb: 2, // Adds some space between the items
                  }}
               >
                  <Box
                     sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "50%", // Box takes 50% of the page width
                        border: "1px solid #c0c0c0", // Border around the entire left box
                        alignItems: "flex-start", // Center content vertically
                        padding: '10px', // Add padding inside the box
                     }}
                  >
                     <Box
                        sx={{
                           width: "25%", // Icon area takes 25% of the box
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                        }}
                     >
                        <img
                           src={option.image}
                           alt={`${option.name} icon`}
                           style={{
                              maxWidth: "100%",
                              maxHeight: "60px", // Limit the height of the image
                              objectFit: "contain",
                           }}
                        />
                     </Box>
                     <Box
                        sx={{
                           width: "75%", // Text area takes 75% of the box
                           paddingLeft: 2, // Adds some padding on the left
                           display: "flex",
                           flexDirection: "column",
                           justifyContent: "top", // Center text vertically
                        }}
                     >
                        <StyledTypography variant="body1" sx={{fontWeight: 'bold'}}>
                           {option.name}
                        </StyledTypography>
                        <StyledTypography variant="body1">
                           {option.color}
                        </StyledTypography>
                     </Box>
                  </Box>
                  <Box
                     sx={{
                        display: "flex",
                        flexDirection: "row",
                        width: "50%", // Box takes 50% of the page width
                        border: "1px solid #c0c0c0", // Border around the entire right box
                        alignItems: "center", // Center content vertically
                        padding: '10px', // Add padding inside the box
                     }}
                  >
                     <Controller
                        name={`items.${index}.comment`}
                        control={control}
                        render={({field}) => (
                           <textarea
                              {...field}
                              style={{
                                 width: "100%", // Full width of the container
                                 height: "100%", // Match height of the left box
                                 border: "none", // Remove border from the textarea
                                 resize: "none", // Disable manual resizing
                                 outline: "none", // Remove the default outline
                                 padding: "0", // Remove padding for a cleaner look
                                 fontFamily: "inherit", // Match font with surrounding text
                                 fontSize: "inherit", // Match font size
                                 lineHeight: "1.5", // Adjust line height for better readability,
                                 background: 'transparent',
                              }}
                              maxLength={500} // Limit the number of characters
                              placeholder={localizations.commentsPagePlaceholder}
                              rows={2} // Adjust the number of rows for multiline input
                           />
                        )}
                     />
                  </Box>
               </Box>
            ))}
            <Box
               sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: "100%", // Box takes 100% of the page width
                  gap: 2,
                  alignItems: "stretch", // Stretch items to match height
                  mb: 2, // Adds some space between the items
               }}
            >
               <Box
                  sx={{
                     display: "flex",
                     flexDirection: "row",
                     width: "100%", // Box takes 50% of the page width
                     alignItems: "center", // Center content vertically
                     margin: '30px 0 0 0',
                     justifyContent: "center",
                     border: animation ? '2px solid rgb(211, 47, 47)' : '2px solid transparent', borderRadius: '4px'
                  }}
               >
                  {localizations.commentsPageAccept &&
                     <FormInputCheckbox disabled={!hasComments} name={"commentsAccept"}
                                        label={localizations.commentsPageAccept}/>
                  }
               </Box>
            </Box>
         </FormProvider>
      </Box>
   );
};
